import { ISelectedShiftReducer, IShift } from 'models/Shift'
import { Action } from 'redux'
import { isType } from 'ts-action'
import { IError } from 'models/Global'
import { deleteFromArray } from '@src/helpers/helperFunctions'
import { IShiftNote } from '@src/models/ShiftNote'
import {
  GetSelectedShift,
  UpdateSelectedShift,
  DeleteSelectedShift,
  SelectedShiftFetching,
  ResetSelectedShift,
  GetSelectedShiftErrors,
  AddShiftNote,
  DeleteShiftNote,
  UpdateShiftNote,
  PutSelectedShiftLock,
} from './actions'

const initialState: ISelectedShiftReducer = {
  meta: {
    code: 0,
    message: '',
  },
  isFetching: false,
  error: {} as IError,
  data: {} as IShift,
  shiftNotes: [],
}

export const selectedShiftReducer = (
  state = initialState,
  action: Action
): ISelectedShiftReducer => {
  if (isType(action, GetSelectedShift)) {
    const {
      payload: { data, meta },
    } = action

    return {
      ...state,
      meta,
      data,
      error: {} as IError,
    }
  }

  if (isType(action, UpdateSelectedShift)) {
    const {
      payload: {
        response: { data, meta },
        shiftNotesResponse: { data: shiftNotes },
      },
    } = action

    return {
      ...state,
      meta,
      data,
      shiftNotes,
      error: {} as IError,
    }
  }

  if (isType(action, AddShiftNote)) {
    const { payload } = action
    const { note, shiftId } = payload
    const { data } = note

    if (data.parent_id) {
      const parentNoteIndex = state.shiftNotes.findIndex((note) => note.id === data.parent_id)
      if (parentNoteIndex !== -1) {
        const parentNote = state.shiftNotes[parentNoteIndex]
        const updatedParentNote = {
          ...parentNote,
          replies: { data: [data, ...(parentNote.replies?.data || [])] },
        }

        const updatedShiftNotes = [...state.shiftNotes]
        updatedShiftNotes[parentNoteIndex] = updatedParentNote

        return {
          ...state,
          shiftNotes: updatedShiftNotes,
        }
      }
      return state
    }

    return {
      ...state,
      shiftNotes: [data, ...state.shiftNotes],
    }
  }

  if (isType(action, DeleteShiftNote)) {
    const { payload } = action
    const { noteId, shiftId, isReply } = payload
    const { shiftNotes } = state

    // Try removing from top-level notes first
    const topLevelIndex = shiftNotes.findIndex((note) => note.id === noteId)
    if (topLevelIndex !== -1) {
      const formattedArray = deleteFromArray<IShiftNote>(shiftNotes, noteId)
      return {
        ...state,
        shiftNotes: formattedArray,
      }
    }

    // If not found in top-level, search within replies of each parent note
    let foundInReplies = false
    const updatedShiftNotes = shiftNotes.map((parentNote) => {
      if (foundInReplies || !parentNote.replies?.data?.length) return parentNote

      const replyIndex = parentNote.replies.data.findIndex((reply) => reply.id === noteId)
      if (replyIndex === -1) return parentNote

      foundInReplies = true
      const updatedReplies = deleteFromArray<IShiftNote>(parentNote.replies.data, noteId)
      return {
        ...parentNote,
        replies: { data: updatedReplies },
      }
    })

    if (foundInReplies) {
      return {
        ...state,
        shiftNotes: updatedShiftNotes,
      }
    }

    return state
  }

  if (isType(action, UpdateShiftNote)) {
    const { payload } = action
    const { id, note } = payload
    const { shiftNotes } = state
    // First try to update a top-level note
    const topLevelIndex = shiftNotes.findIndex((n) => n.id === id)
    if (topLevelIndex !== -1) {
      const updated = [...shiftNotes]
      const currentNote = updated[topLevelIndex]
      const currentVersionCount = currentNote.versions_count || 0
      updated[topLevelIndex] = { ...currentNote, note, versions_count: currentVersionCount + 1 }
      return { ...state, shiftNotes: updated }
    }

    // If not found, try to update within replies
    let updatedInReplies = false
    const updatedShiftNotes = shiftNotes.map((parentNote) => {
      if (updatedInReplies || !parentNote.replies?.data?.length) return parentNote

      const replyIndex = parentNote.replies.data.findIndex((reply) => reply.id === id)
      if (replyIndex === -1) return parentNote

      updatedInReplies = true
      const updatedReplies = [...parentNote.replies.data]
      const currentReply = updatedReplies[replyIndex]
      const currentVersionCount = currentReply.versions_count || 0
      updatedReplies[replyIndex] = {
        ...currentReply,
        note,
        versions_count: currentVersionCount + 1,
      }
      return { ...parentNote, replies: { data: updatedReplies } }
    })

    if (updatedInReplies) {
      return { ...state, shiftNotes: updatedShiftNotes }
    }

    return state
  }

  if (isType(action, DeleteSelectedShift)) {
    return initialState
  }

  if (isType(action, SelectedShiftFetching)) {
    const { payload: isFetching } = action

    return {
      ...state,
      isFetching,
    }
  }

  if (isType(action, ResetSelectedShift)) {
    return initialState
  }

  if (isType(action, GetSelectedShiftErrors)) {
    const {
      payload: { code, message },
    } = action

    return {
      ...state,
      error: {
        code,
        message,
      },
    }
  }

  if (isType(action, PutSelectedShiftLock)) {
    const { payload } = action
    return {
      ...state,
      data: {
        ...state.data,
        is_shift_locked: payload,
      },
    }
  }

  return state
}
